import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { apiGet } from '../api/client';

// Only reads the application's authorized local-cache endpoint. No sync/retry side effects.
export function useLocalList<T>(path: string) {
  const [state, setState] = useState<{ path: string; items: T[]; error: string; loading: boolean }>({
    path,
    items: [],
    error: '',
    loading: true
  });
  const generation = useRef(0);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setState((old) => ({ path, items: old.path === path ? old.items : [], error: '', loading: true }));
    try {
      const items = await apiGet<T[]>(path);
      if (!mounted.current || request !== generation.current) return undefined;
      setState({ path, items, error: '', loading: false });
      return;
    } catch (error) {
      if (!mounted.current || request !== generation.current) return undefined;
      const status = (error as { status?: number }).status;
      setState((old) => ({
        path,
        items: old.path === path && status !== 401 && status !== 403 ? old.items : [],
        error: error instanceof Error ? error.message : '读取失败，请重试',
        loading: false
      }));
      return undefined;
    }
  }, [path]);
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, [load]);
  const setItems = useCallback(
    (next: SetStateAction<T[]>) => {
      setState((old) => ({
        ...old,
        path,
        items: typeof next === 'function' ? (next as (rows: T[]) => T[])(old.path === path ? old.items : []) : next
      }));
    },
    [path]
  );
  return {
    items: state.path === path ? state.items : [],
    error: state.path === path ? state.error : '',
    loading: state.path !== path || state.loading,
    load,
    setItems
  };
}
